import './Modal.scss'

import clsx from 'clsx'
import { useState } from 'react'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { CUSTOMER_REASONS } from '../../consts'
import { postUpdateIdentifier } from '../../features/customer/customerSlice'
import { CheckIcon } from '../../GlobalComponents/_icons/Icons'
import { FastQuoteInput } from '../../GlobalComponents/Inputs/Inputs'
import styles from './IdentifierModal.module.scss'

interface IdentifierModalProps {
    close(): void
    submit(projectName: string): void
}
interface ButtonProps {
    className?: string
    selected: boolean
    onClick(): void
    children: any
    testid?: any
}
const IdentifierButton = (props: ButtonProps) => {
    const { selected, onClick, children } = props
    let buttonClass = clsx([styles.button, 'a-button centered'])
    let selectedButtonClass = clsx([styles.selectedButton, 'a-button centered'])
    return (
        <div
            className={selected ? selectedButtonClass : buttonClass}
            onClick={() => !selected && onClick()}
            data-testid={props.testid}
        >
            {selected && <CheckIcon />}
            {children}
        </div>
    )
}
const IdentifierModal = (props: IdentifierModalProps) => {
    const { close } = props
    const customer = useAppSelector((state) => state.customer.currentCustomer!)

    const [projectName, setProjectName] = useState(customer.CustomerReasonQualifier)
    const [invalidProjectName, setInvalidProjectName] = useState(false)
    const dispatch = useAppDispatch()
    const onSelect = (reasonId: number, qualifier: string = '') => {
        dispatch(postUpdateIdentifier({ accountCode: customer.AccountCode, reasonId, qualifier }))
        close()
    }
    return (
        <div className={styles.identifierModal} data-testid="identifierModal">
            <div className={styles.identifierModalHeader}>
                <div className={styles.identifierTitle}>Select Identifier</div>
                <div onClick={close} className={'a-i-close'} data-testid="identifierModalClose" />
            </div>
            <div className={'a-divider'} />

            <div className={styles.identifierRow}>
                <div className={styles.identifierTitle} data-testid="identifierTitleStandard">
                    Standard
                </div>
                <div className={''}>
                    The Standard Account defines our most common Sales Order, Invoicing and Payment
                    Terms with a customer.
                </div>
                <IdentifierButton
                    testid="identifierModalStandardButton"
                    selected={customer.ReasonName === CUSTOMER_REASONS.STANDARD.reasonName}
                    onClick={() => onSelect(CUSTOMER_REASONS.STANDARD.reasonId)}
                >
                    {customer.ReasonName === CUSTOMER_REASONS.STANDARD.reasonName
                        ? 'Selected'
                        : 'Select'}
                </IdentifierButton>
            </div>

            <div className={'a-divider'} style={{ marginTop: 20 }} />

            <div className={styles.identifierRowNoButton}>
                <div className={styles.identifierTitle}>Specialty</div>
                <div className={''}>
                    A Specialty Account may be established when we agree to follow special terms for
                    a subset of the customer's business.
                    <div className={'a-divider'} style={{ marginTop: 20 }} />
                    <div className={styles.identifierRow} style={{ fontWeight: 500 }}>
                        <div></div>
                        <div>Identify the reason for the Specialty Account:</div>
                        <div></div>
                    </div>
                    <div className={styles.identifierRow}>
                        <div className={styles.identifierTitle}>Credit Card</div>
                        <div className={''}>
                            For occasional credit card orders for a customer who usually buys on an
                            open account.
                        </div>
                        <IdentifierButton
                            testid="identifierModalCreditCardButton"
                            selected={
                                customer.ReasonName === CUSTOMER_REASONS.CREDIT_CARD.reasonName
                            }
                            onClick={() => onSelect(CUSTOMER_REASONS.CREDIT_CARD.reasonId)}
                        >
                            {customer.ReasonName === CUSTOMER_REASONS.CREDIT_CARD.reasonName
                                ? 'Selected'
                                : 'Select'}
                        </IdentifierButton>

                        <div className={styles.identifierTitle}>Consumables</div>
                        <div className={''}>
                            For occasional orders of material to be consumed by a Reseller or OEM
                            with a Tax-Exempt Certificate on file.
                        </div>

                        <IdentifierButton
                            testid="identifierModalConsumablesButton"
                            selected={
                                customer.ReasonName === CUSTOMER_REASONS.CONSUMABLES.reasonName
                            }
                            onClick={() => onSelect(CUSTOMER_REASONS.CONSUMABLES.reasonId)}
                        >
                            {customer.ReasonName === CUSTOMER_REASONS.CONSUMABLES.reasonName
                                ? 'Selected'
                                : 'Select'}
                        </IdentifierButton>
                        <div className={styles.identifierTitle}>VSP</div>
                        <div className={''}>
                            For occasional orders where AWC is responsible for managing{' '}
                            <b>customer owned</b> inventory at the customer's location. (Vendor
                            Stocking Program)
                        </div>
                        <IdentifierButton
                            testid="identifierModalVSPButton"
                            selected={customer.ReasonName === CUSTOMER_REASONS.VSP.reasonName}
                            onClick={() => onSelect(CUSTOMER_REASONS.VSP.reasonId)}
                        >
                            {customer.ReasonName === CUSTOMER_REASONS.VSP.reasonName
                                ? 'Selected'
                                : 'Select'}
                        </IdentifierButton>
                        <div className={styles.identifierTitle}>Consignment</div>
                        <div className={''}>
                            For occasional orders where AWC is responsible for managing{' '}
                            <b>AWC owned</b> inventory at the customer's location.
                        </div>
                        <IdentifierButton
                            testid="identifierModalConsignmentButton"
                            selected={
                                customer.ReasonName === CUSTOMER_REASONS.CONSIGNMENT.reasonName
                            }
                            onClick={() => onSelect(CUSTOMER_REASONS.CONSIGNMENT.reasonId)}
                        >
                            {customer.ReasonName === CUSTOMER_REASONS.CONSIGNMENT.reasonName
                                ? 'Selected'
                                : 'Select'}
                        </IdentifierButton>
                        <div className={styles.identifierTitle} style={{ alignSelf: 'center' }}>
                            Other
                        </div>
                        <FastQuoteInput
                            placeholder="Enter Project Name"
                            className={styles.otherInput}
                            value={projectName}
                            maxLength={50}
                            onChange={(value: string) => setProjectName(value)}
                            validationText={invalidProjectName ? '* Required' : ''}
                            testid="identifierModalProjectName"
                        />
                        <div></div>
                        <div></div>
                        <div>
                            For temporary projects where AWC agreed to Sales Order, Invoicing,
                            Payment Terms, Reporting or Sales Credit Assignments that differ from
                            those outlined in the Standard Account.
                        </div>

                        <IdentifierButton
                            testid="identifierModalOtherButton"
                            selected={
                                customer.ReasonName === CUSTOMER_REASONS.OTHER.reasonName &&
                                customer.CustomerReasonQualifier === projectName
                            }
                            onClick={() => {
                                if (!projectName) setInvalidProjectName(true)
                                else onSelect(CUSTOMER_REASONS.OTHER.reasonId, projectName)
                            }}
                        >
                            {customer.ReasonName === CUSTOMER_REASONS.OTHER.reasonName
                                ? customer.CustomerReasonQualifier === projectName
                                    ? 'Selected'
                                    : 'Update'
                                : 'Select'}
                        </IdentifierButton>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default IdentifierModal
